import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveManualTradingLegs, type RealtimeSpreadDirection } from '@/lib/manualTradingLegs';
import { isManualTradingConfigured, listHistoryTasks, type TaskView } from '@/services/manualTradingApi';
import { InFlightTaskTable } from './InFlightTaskTable';
import './ManualTradingPanel.css';

type Props = {
  symbol: string;
  exchange1: string;
  exchange2: string;
  realtimeDirection: RealtimeSpreadDirection;
  enabled: boolean;
};

export function PairHistoryTasksPanel({
  symbol,
  exchange1,
  exchange2,
  realtimeDirection,
  enabled,
}: Props) {
  const [historyTasks, setHistoryTasks] = useState<TaskView[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const legs = useMemo(
    () => resolveManualTradingLegs(exchange1, exchange2, realtimeDirection),
    [exchange1, exchange2, realtimeDirection],
  );

  const pairQuery = useMemo(() => {
    if (!legs.ok || !symbol.trim()) return null;
    return {
      symbol: symbol.trim(),
      long_exchange: legs.long_exchange,
      short_exchange: legs.short_exchange,
    };
  }, [legs, symbol]);

  const refreshHistoryTasks = useCallback(async () => {
    if (!isManualTradingConfigured() || !pairQuery) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const rows = await listHistoryTasks(pairQuery);
      setHistoryTasks(rows.sort((a, b) => b.created_at - a.created_at));
    } catch (e) {
      setHistoryError(e instanceof Error ? e.message : String(e));
      setHistoryTasks([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [pairQuery]);

  useEffect(() => {
    if (!enabled || !isManualTradingConfigured()) return;
    if (!pairQuery) {
      setHistoryTasks([]);
      setHistoryError(legs.ok ? null : legs.error);
      return;
    }
    void refreshHistoryTasks();
  }, [enabled, refreshHistoryTasks, pairQuery, legs]);

  return (
    <div className="realtime-spread-positions-history">
      {!legs.ok ? <p className="error">{legs.error}</p> : null}
      <InFlightTaskTable
        tasks={historyTasks}
        loading={historyLoading}
        error={historyError}
        emptyHint="当前交易对暂无历史成交记录。"
        finishedAtLabel="完成(UTC)"
        variant="history"
        onRefresh={refreshHistoryTasks}
        hideToolbar
      />
    </div>
  );
}
